import React, { useRef, useState } from 'react';
import { toast } from 'sonner';
import { useT } from '../../i18n/LanguageContext';
import { Upload, Loader2, FileSpreadsheet, AlertTriangle, CheckCircle2, XCircle, Users } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../ui/select';
import { schoolApi } from '../../api/school';

const stateBadge = (state, t) => {
  switch (state) {
    case 'new': return <Badge className="bg-emerald-600 hover:bg-emerald-600">{t('imp.new', 'New')}</Badge>;
    case 'already': return <Badge variant="secondary">{t('imp.already', 'Already correct')}</Badge>;
    case 'conflict': return <Badge className="bg-orange-500 hover:bg-orange-500 text-white">{t('imp.conflict', 'Conflict')}</Badge>;
    case 'missing_class': return <Badge variant="destructive">{t('imp.missingClass', 'Missing class')}</Badge>;
    default: return <Badge variant="destructive">{t('imp.unmatched', 'Unmatched')}</Badge>;
  }
};

export const ImportEnrollmentTool = ({ orgId, academicYearId, onImported }) => {
  const { t } = useT();
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [open, setOpen] = useState(false);
  const [globalRes, setGlobalRes] = useState('skip');
  const [overrides, setOverrides] = useState({});
  const [applying, setApplying] = useState(false);
  const [result, setResult] = useState(null);

  const onPick = () => fileRef.current && fileRef.current.click();

  const onFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file) return;
    setUploading(true);
    setPreview(null); setResult(null); setOverrides({}); setGlobalRes('skip');
    try {
      const data = await schoolApi.importEnrollmentPreview(orgId, academicYearId, file);
      data._file = file;
      setPreview(data);
      setOpen(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('imp.previewFailed', 'Could not read the file'));
    } finally { setUploading(false); }
  };

  const apply = async () => {
    if (!preview) return;
    setApplying(true);
    try {
      const res = await schoolApi.importEnrollmentApply({
        organization_id: orgId,
        academic_year_id: preview.academic_year_id,
        file_name: preview.file_name,
        conflict_resolution: globalRes,
        row_overrides: overrides,
        rows: preview.rows,
      });
      setResult(res);
      toast.success(t('imp.done', `Imported: ${res.summary.students_enrolled} enrolled, ${res.summary.paired_created} paired`));
      onImported && onImported();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('imp.applyFailed', 'Import failed'));
    } finally { setApplying(false); }
  };

  const c = preview?.counts || {};

  return (
    <>
      <input ref={fileRef} type="file" accept=".xls,.xlsx" className="hidden" onChange={onFile} data-testid="import-enrollment-file-input" />
      <Button variant="outline" size="sm" className="gap-2" onClick={onPick} disabled={uploading} data-testid="import-enrollment-button">
        {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
        {t('imp.importBtn', 'Import Enrollment')}
      </Button>

      <Dialog open={open} onOpenChange={(o) => { if (!o && !applying) { setOpen(false); } }}>
        <DialogContent className="max-w-5xl" data-testid="import-enrollment-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><FileSpreadsheet className="w-5 h-5" /> {t('imp.title', 'Import Enrollment from Excel')}</DialogTitle>
            <DialogDescription>
              {result ? t('imp.resultDesc', 'Import complete. Summary below.')
                : t('imp.previewDesc', 'Review the parsed rows before importing. Nothing is changed until you confirm.')}
              {preview?.file_name ? ` · ${preview.file_name}` : ''}{preview?.academic_year_name ? ` · ${preview.academic_year_name}` : ''}
            </DialogDescription>
          </DialogHeader>

          {result ? (
            /* ---------- SUMMARY ---------- */
            <div className="space-y-4" data-testid="import-summary">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[
                  ['students_enrolled', t('imp.enrolled', 'Enrolled'), 'text-emerald-600'],
                  ['students_updated', t('imp.updated', 'Updated'), 'text-orange-500'],
                  ['paired_created', t('imp.pairedCreated', 'Paired created'), 'text-sky-600'],
                  ['skipped_already', t('imp.skipped', 'Skipped (already)'), 'text-muted-foreground'],
                  ['conflicts_skipped', t('imp.conflictsSkipped', 'Conflicts skipped'), 'text-muted-foreground'],
                  ['unmatched_count', t('imp.unmatchedC', 'Unmatched'), 'text-destructive'],
                  ['missing_classes_count', t('imp.missingC', 'Missing classes'), 'text-destructive'],
                  ['error_count', t('imp.errors', 'Errors'), 'text-destructive'],
                ].map(([k, label, cls]) => (
                  <div key={k} className="rounded-lg border border-border p-3">
                    <div className={`text-2xl font-semibold tabular-nums ${cls}`}>{result.summary[k]}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">{label}</div>
                  </div>
                ))}
              </div>
              {result.errors?.length ? (
                <div className="rounded-lg border border-destructive/40 p-3 max-h-40 overflow-auto">
                  <div className="text-sm font-medium text-destructive mb-1">{t('imp.errors', 'Errors')}</div>
                  <ul className="text-xs text-muted-foreground list-disc pl-4 space-y-0.5">{result.errors.map((e, i) => <li key={i}>{e}</li>)}</ul>
                </div>
              ) : null}
            </div>
          ) : preview ? (
            /* ---------- PREVIEW ---------- */
            <div className="space-y-3">
              <div className="flex flex-wrap gap-2 text-xs">
                <Badge className="bg-emerald-600 hover:bg-emerald-600" data-testid="count-new">{t('imp.new', 'New')}: {c.new || 0}</Badge>
                <Badge variant="secondary" data-testid="count-already">{t('imp.already', 'Already correct')}: {c.already || 0}</Badge>
                <Badge className="bg-orange-500 hover:bg-orange-500 text-white" data-testid="count-conflict">{t('imp.conflict', 'Conflicts')}: {c.conflict || 0}</Badge>
                <Badge variant="destructive" data-testid="count-unmatched">{t('imp.unmatched', 'Unmatched')}: {c.unmatched || 0}</Badge>
                <Badge variant="destructive" data-testid="count-missing">{t('imp.missingClass', 'Missing class')}: {c.missing_class || 0}</Badge>
              </div>

              {c.conflict ? (
                <div className="flex items-center gap-3 rounded-lg border border-orange-500/30 bg-orange-500/5 p-2.5">
                  <AlertTriangle className="w-4 h-4 text-orange-500 shrink-0" />
                  <span className="text-sm">{t('imp.conflictHelp', 'For conflicts, choose globally:')}</span>
                  <div className="w-40">
                    <Select value={globalRes} onValueChange={setGlobalRes}>
                      <SelectTrigger data-testid="global-conflict-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="skip">{t('imp.skip', 'Skip')}</SelectItem>
                        <SelectItem value="update">{t('imp.update', 'Update to file')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              ) : null}

              <div className="max-h-[42vh] overflow-auto border border-border rounded-lg">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-background"><tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
                    <th className="px-2 py-2">{t('imp.student', 'Student (file)')}</th>
                    <th className="px-2 py-2">{t('imp.match', 'Matched')}</th>
                    <th className="px-2 py-2">{t('imp.classSec', 'Class / Section')}</th>
                    <th className="px-2 py-2">{t('imp.state', 'State')}</th>
                    <th className="px-2 py-2">{t('imp.paired', 'Paired class')}</th>
                    <th className="px-2 py-2">{t('imp.decision', 'Decision')}</th>
                  </tr></thead>
                  <tbody>
                    {preview.rows.map((r) => (
                      <tr key={r.row_index} className={`border-b border-border last:border-0 ${r.enroll_state === 'conflict' ? 'bg-orange-500/5' : ''}`} data-testid={`import-row-${r.row_index}`}>
                        <td className="px-2 py-2 font-medium">{r.file_name}</td>
                        <td className="px-2 py-2">
                          {r.match_status === 'matched'
                            ? <span className="inline-flex items-center gap-1 text-emerald-600"><CheckCircle2 className="w-3.5 h-3.5" />{r.student_name}</span>
                            : <span className="text-destructive text-xs">{r.match_status === 'ambiguous' ? t('imp.ambiguous', 'Multiple matches') : t('imp.noMatch', 'No match')}</span>}
                        </td>
                        <td className="px-2 py-2">
                          <div>{r.class_name || <span className="text-destructive">{r.file_class}</span>}</div>
                          <div className="text-xs text-muted-foreground">{r.section_name || '—'}{!r.section_exists && r.class_exists ? ` (${t('imp.willCreate', 'will create')})` : ''}</div>
                        </td>
                        <td className="px-2 py-2">
                          {stateBadge(r.enroll_state, t)}
                          {r.enroll_state === 'conflict' ? (
                            <div className="text-[11px] text-orange-600 mt-1">{t('imp.currently', 'Now')}: {r.current_class_name || '—'} / {r.current_section_name || '—'}</div>
                          ) : null}
                        </td>
                        <td className="px-2 py-2 text-xs text-muted-foreground">
                          {r.paired_class_name ? <span>{r.paired_class_name}{r.paired_state === 'already' ? ` · ${t('imp.already', 'already')}` : ` · ${t('imp.new', 'new')}`}</span> : '—'}
                        </td>
                        <td className="px-2 py-2">
                          {r.enroll_state === 'conflict' ? (
                            <Select value={overrides[String(r.row_index)] || globalRes} onValueChange={(v) => setOverrides((o) => ({ ...o, [String(r.row_index)]: v }))}>
                              <SelectTrigger className="h-8 w-28" data-testid={`row-decision-${r.row_index}`}><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="skip">{t('imp.skip', 'Skip')}</SelectItem>
                                <SelectItem value="update">{t('imp.update', 'Update')}</SelectItem>
                              </SelectContent>
                            </Select>
                          ) : <span className="text-xs text-muted-foreground">—</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {(preview.unmatched_names?.length || preview.missing_classes?.length) ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="rounded-lg border border-border p-3">
                    <div className="text-sm font-medium mb-1 flex items-center gap-1.5"><Users className="w-4 h-4" /> {t('imp.needResolve', 'Unmatched names')} ({preview.unmatched_names.length})</div>
                    <div className="text-xs text-muted-foreground max-h-28 overflow-auto">{preview.unmatched_names.join(', ') || '—'}</div>
                  </div>
                  <div className="rounded-lg border border-border p-3">
                    <div className="text-sm font-medium mb-1 flex items-center gap-1.5"><XCircle className="w-4 h-4" /> {t('imp.classesMissing', 'Classes not in system')} ({preview.missing_classes.length})</div>
                    <div className="text-xs text-muted-foreground max-h-28 overflow-auto">{preview.missing_classes.join(', ') || '—'}</div>
                  </div>
                </div>
              ) : null}
            </div>
          ) : null}

          <DialogFooter>
            {result ? (
              <Button onClick={() => setOpen(false)} data-testid="import-result-close">{t('common.close', 'Close')}</Button>
            ) : (
              <>
                <Button variant="outline" onClick={() => setOpen(false)} disabled={applying} data-testid="import-cancel">{t('common.cancel', 'Cancel')}</Button>
                <Button onClick={apply} disabled={applying || !preview || (!c.new && !c.conflict && !c.already)} className="gap-2" data-testid="import-confirm-button">
                  {applying ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                  {applying ? t('imp.importing', 'Importing…') : t('imp.confirmImport', 'Confirm & Import')}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ImportEnrollmentTool;
